import React from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config/settings';

const CHECK_INTERVAL = 5000; // 5 seconds
const STABLE_FOR_MS = 3000; // ms
const TIMEOUT_MS = 180000; // 3 minutes

// Minimalist splash: spinner + single status line

const StartSplash = () => {
  const navigate = useNavigate();
  // No query params, fixed timings for kiosk simplicity

  // Use config baseUrl which already has auto-detection logic
  const API_BASE = (config?.api?.baseUrl || (typeof window !== 'undefined' ? `http://${window.location.hostname}:5000` : '')).replace(/\/$/, '');
  if (!API_BASE) {
    console.error('[StartSplash] Backend URL not configured');
  } else {
    console.log(`[StartSplash] Using backend URL: ${API_BASE} (detected from: ${typeof window !== 'undefined' ? window.location.href : 'N/A'})`);
  }
  const HEALTH_URL = `${API_BASE}/api/health`;
  const CACHE_STATUS_URL = `${API_BASE}/api/cache-status`;
  // Now also check cache readiness (products + users)

  const [backendOk, setBackendOk] = React.useState(false);
  const [ready, setReady] = React.useState(false);
  const [dots, setDots] = React.useState('');
  const [statusMain, setStatusMain] = React.useState('Preparing kiosk…');
  const [timeoutHit, setTimeoutHit] = React.useState(false);
  const [lastError, setLastError] = React.useState('');
  const [diag, setDiag] = React.useState({
    productsReady: false,
    usersReady: false,
    fromCache: null,
    lastChecked: null,
    cacheError: null
  });

  const handleRetryNow = React.useCallback(async () => {
    try {
      const [healthRes, cacheRes] = await Promise.all([
        fetch(HEALTH_URL, { cache: 'no-store' }),
        fetch(CACHE_STATUS_URL, { cache: 'no-store' })
      ]);

      const ok = healthRes.ok;
      setBackendOk(ok);

      let productsReady = false;
      let usersReady = false;
      let cachesReady = false;
      let cacheError = null;

      if (cacheRes.ok) {
        const cacheJson = await cacheRes.json();
        productsReady = !!cacheJson?.products?.ready;
        usersReady = !!cacheJson?.users?.ready;
        cachesReady = productsReady;
        cacheError = cacheJson?.products?.lastError || cacheJson?.users?.lastError || null;
      } else {
        cacheError = `cache-status HTTP ${cacheRes.status}`;
      }

      setDiag({
        productsReady,
        usersReady,
        fromCache: true,
        lastChecked: new Date().toLocaleTimeString(),
        cacheError
      });

      if (ok && cachesReady) {
        setStatusMain(usersReady ? 'Products ready. Warming profiles…' : 'Cache ready. Launching…');
        navigate('/', { replace: true });
      } else {
        setStatusMain('Preparing kiosk… (warming cache)');
      }
    } catch (err) {
      setBackendOk(false);
      setLastError(err?.message || String(err));
      setDiag(prev => ({ ...prev, lastChecked: new Date().toLocaleTimeString(), cacheError: err?.message || String(err) }));
    }
  }, [HEALTH_URL, CACHE_STATUS_URL, navigate]);

  // Animated dots
  React.useEffect(() => {
    const t = setInterval(() => {
      setDots((d) => (d.length >= 3 ? '' : d + '.'));
    }, 500);
    return () => clearInterval(t);
  }, []);

  // No network listeners or diagnostics overlay in simplified flow

  React.useEffect(() => {
    let intervalId;
    let stableSince = null;
    let redirected = false;
    let redirectTimeout;
    let timeoutTimer;

    const check = async () => {
      try {
        const [healthRes, cacheRes] = await Promise.all([
          fetch(HEALTH_URL, { cache: 'no-store' }),
          fetch(CACHE_STATUS_URL, { cache: 'no-store' })
        ]);

        const ok = healthRes.ok;
        setBackendOk(ok);
        setLastError('');

        let cachesReady = false;
        let usersWarming = false;
        if (cacheRes.ok) {
          const cacheJson = await cacheRes.json();
          const productsReady = !!cacheJson?.products?.ready;
          const usersReady = !!cacheJson?.users?.ready;
          // Proceed when products are ready; let users continue warming in background
          cachesReady = productsReady;
          usersWarming = productsReady && !usersReady;
          setDiag({
            productsReady,
            usersReady,
            fromCache: true,
            lastChecked: new Date().toLocaleTimeString(),
            cacheError: cacheJson?.products?.lastError || cacheJson?.users?.lastError || null
          });
          if (cachesReady) {
            setStatusMain(usersWarming ? 'Products ready. Warming profiles…' : 'Cache ready. Launching…');
          } else {
            setStatusMain('Preparing kiosk… (warming cache)');
          }
        } else {
          setStatusMain('Preparing kiosk…');
          setDiag(prev => ({ ...prev, lastChecked: new Date().toLocaleTimeString(), cacheError: `cache-status HTTP ${cacheRes.status}` }));
        }

        if (ok && cachesReady) {
          if (!stableSince) {
            stableSince = Date.now();
          }
          const stableMs = Date.now() - stableSince;
          if (stableMs >= STABLE_FOR_MS && !redirected) {
            setReady(true);
            setStatusMain('Launching kiosk…');
            redirected = true;
            clearTimeout(timeoutTimer);
            navigate('/', { replace: true });
          }
        } else {
          stableSince = null;
          setReady(false);
        }
      } catch (err) {
        stableSince = null;
        setReady(false);
        setBackendOk(false);
        setLastError(err?.message || String(err));
        setStatusMain('Preparing kiosk…');
        setDiag(prev => ({ ...prev, lastChecked: new Date().toLocaleTimeString(), cacheError: err?.message || String(err) }));
      }
    };

    // initial check immediately, then interval
    check();
    intervalId = setInterval(check, CHECK_INTERVAL);

    // global timeout to show friendly message
    timeoutTimer = setTimeout(() => {
      setTimeoutHit(true);
      setStatusMain('Unable to start kiosk. Please contact an attendant.');
      // Stop further checks on timeout
      if (intervalId) clearInterval(intervalId);
    }, TIMEOUT_MS);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (redirectTimeout) clearTimeout(redirectTimeout);
      if (timeoutTimer) clearTimeout(timeoutTimer);
    };
  }, [HEALTH_URL, CHECK_INTERVAL, STABLE_FOR_MS, TIMEOUT_MS, navigate]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      width: '100vw',
      background: 'linear-gradient(180deg, #0b0f17 0%, #0e1624 100%)',
      color: '#e6e8ec',
      fontFamily: 'Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
      textAlign: 'center',
      padding: 24,
      cursor: 'none'
    }}>
      <img
        src="/assets/images/ICCT-Logo-trans.webp"
        alt="ICCT Logo"
        style={{ width: 120, height: 'auto', marginBottom: 12, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.5))' }}
        draggable={false}
      />
      <div style={{ fontSize: 36, fontWeight: 700, color: '#e6e8ec', marginBottom: 6, letterSpacing: 0.3 }}>
        ICCT Kiosk
      </div>
      <div style={{ fontSize: 18, color: '#9aa4b2', marginBottom: 28 }}>
        {statusMain}
      </div>

      {/* Spinner */}
      <div style={{
        width: 84,
        height: 84,
        borderRadius: '50%',
        border: '5px solid #1f2940',
        borderTopColor: ready ? '#22c55e' : backendOk ? '#3b82f6' : '#f59e0b',
        animation: 'spin 1s linear infinite',
        boxShadow: '0 0 0 3px rgba(31,41,64,0.35)'
      }} />

      {/* Minimal status only; no progress bar or chips */}
      {timeoutHit && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontSize: 16, color: '#fda4af', marginBottom: 10 }}>
            Unable to start kiosk. Please contact an attendant.
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <button
              onClick={handleRetryNow}
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Retry Now
            </button>
            <button
              onClick={() => navigate('/', { replace: true })}
              style={{
                background: '#22c55e',
                color: '#0b0f17',
                border: 'none',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Continue Anyway
            </button>
          </div>
        </div>
      )}

      {/* Diagnostics panel (temporary) */}
      <div style={{
        marginTop: 24,
        width: 'min(680px, 92vw)',
        background: 'rgba(255,255,255,0.06)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: 8,
        padding: 12,
        textAlign: 'left',
        fontSize: 13,
        color: '#cbd5e1'
      }}>
        <div style={{ fontWeight: 700, marginBottom: 6, color: '#e2e8f0' }}>Diagnostics</div>
        <div>Backend: <strong style={{ color: backendOk ? '#22c55e' : '#f59e0b' }}>{backendOk ? 'OK' : 'Waiting'}</strong></div>
        <div>Products ready: <strong style={{ color: diag.productsReady ? '#22c55e' : '#f59e0b' }}>{String(diag.productsReady)}</strong></div>
        <div>Users ready: <strong style={{ color: diag.usersReady ? '#22c55e' : '#f59e0b' }}>{String(diag.usersReady)}</strong></div>
        <div>Last checked: <strong>{diag.lastChecked || '—'}</strong></div>
        {lastError ? (<div style={{ color: '#fda4af' }}>Last error: {lastError}</div>) : null}
        {diag.cacheError ? (<div style={{ color: '#fca5a5' }}>Cache status: {diag.cacheError}</div>) : null}
        <div style={{ opacity: 0.8, marginTop: 6 }}>API Base: {API_BASE}</div>
      </div>


      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        html, body { overscroll-behavior: none; }
      `}</style>
    </div>
  );
};

export default StartSplash;
